/**
 * Quick add: one line of words in, a to-do out.
 *
 * Gordon: "Look at apps like Todoist, google keep and apple reminders." The
 * thing all three get right is that adding a reminder is typing a sentence.
 * "Order the cable tomorrow 3pm p1 #0042" is a title, a day, a time, a
 * priority and a ticket, and nobody should have to open four fields to say
 * it. So the box reads the sentence as it is typed, shows what it understood
 * as chips that can be taken back, and takes those words out of the title.
 *
 * PURE AND IMPORT-FREE, and that is the point of the file. Every date here is
 * worked out from a `now` the caller passes in, never from the clock, so
 * e2e/quick-add.mjs can load this file on its own in Node and check a wide
 * table of phrases against one fixed Tuesday morning. A parser that reads
 * the clock can only be tested on the day it was written.
 *
 * DATES ARE LOCAL CALENDAR DAYS, 'YYYY-MM-DD', computed from `now`'s own
 * year, month and day - never through toISOString, which is UTC and on this
 * side of Greenwich turns a late evening into the day before (the to-do
 * list's own route has a comment about the same trap on the way back).
 *
 * WHAT IT DELIBERATELY DOES NOT READ:
 *   - "sat" and "sun" on their own. They are words ("sat nav", "sun damage")
 *     far more often than days in a contractor's notes; "saturday" and
 *     "sunday" are read.
 *   - "p1" inside a word: "app1" stays "app1". Priority is a word of its own.
 *   - "9.30" with no am/pm, or a bare "9". A version number or a quantity is
 *     likelier than a time. "at 9", "9.30am", "09:30" and "9pm" are times.
 *   - A ticket number that is not one of the contractor's own tickets. The
 *     chip would be a promise the server then refuses.
 * Anything it misreads is one tap on the chip's × to take back, and the
 * words go back into the title.
 *
 * LISTS, SECTIONS AND LABELS, Todoist's way (batch 2, when everybody got a
 * to-do list of their own and more than one of them):
 *   - "#Site visits" picks a list. The rule that keeps it apart from a ticket
 *     is the first character: '#' and a DIGIT is a ticket ("#0042"), '#' and
 *     a LETTER is a list. A list called "2027 plans" therefore cannot be
 *     picked by typing - a fair price for "#42" never being misread.
 *   - "/Quotes" picks a section of that list - or, with no "#", of the list
 *     being looked at.
 *   - "@waiting" adds a label. One that does not exist yet comes back marked
 *     `isNew`, and the page asks before creating it: a typo should not
 *     quietly become a label everybody's list then carries.
 * Lists and sections are matched loosely - "#site" finds "Site visits" when
 * nothing else starts that way - because nobody types a list's name exactly.
 * Labels are matched exactly, because a near miss there is a new label.
 * The lists and labels are passed in, like the tickets: still no imports.
 *
 * REPEATS, Todoist's words again (batch 3, when to-dos learned to come back):
 *   "every day" / "daily", "every weekday", "every weekend", "every monday",
 *   "every mon, wed and fri", "every 2 weeks", "every other week", "every
 *   month", "every 15th", "every last day", "every year", "every 25 dec",
 *   "every 3 days". "every! 3 days" or "after 3 days" counts from when it is
 *   DONE rather than from the date it was due - a filter changed every three
 *   months is due three months after it was actually changed. "until 31 dec"
 *   ends one, and is only read beside a repeat: on its own, "until" is just
 *   a word in somebody's sentence.
 *   "every" is only read with a unit after it that this file knows. "Read
 *   every word", "everyone" and "every 3 printers" stay words.
 *   With no day said, the first time round is the date: "bins every monday"
 *   is due next Monday. Unlike a weekday on its own, "every tuesday" said on
 *   a Tuesday starts TODAY - a repeat starting now is what was meant - unless
 *   the time it was given has already gone.
 *
 * PEOPLE, Todoist's "+Name" (batch 4, when lists could be shared and a to-do
 * given to somebody):
 *   - "+Marco" gives the to-do to Marco. Looked for first among the MEMBERS
 *     of the list it is going into (`members` on the list, as the server
 *     sends it) - there it is an assignment - and only then in `people`, the
 *     caller's directory, where it can only be a SEND: the to-do goes into
 *     Marco's own Inbox, because he cannot see a list he is not in. Which of
 *     the two it is comes back as `assignee.member`.
 *   - Loose, like a list: "+mar" is Marco when nobody else starts that way,
 *     and "+Marco Vella" is matched whole. A '+' inside a word ("C++") or
 *     before a digit ("+356 2122") is not a person.
 *   - Nobody matching leaves the word alone: a typo is not a person.
 *   The directory is passed in like everything else: still no imports.
 *
 * DEADLINES AND DURATIONS (073), Todoist's again:
 *   - "{fri}", "{15 nov}", "{2026-11-13}" is the DEADLINE - the date it must
 *     be done by, apart from the day it is planned for. Braces, because a
 *     second bare date in one line ("ring Rita tomorrow, due friday") would
 *     be anybody's guess. Inside them any date this file reads, and nothing
 *     else: "{maybe}" stays words.
 *   - "for 2h", "for 30m", "for 1h30", "for 1.5 hours", "for an hour", "for
 *     half an hour", "for 90 minutes" is how long it takes. Only after
 *     "for", so "2h" in somebody's sentence is left alone. 5 minutes to 24
 *     hours; anything else stays words.
 */

const pad = (n) => String(n).padStart(2, '0');

/** A local Date as 'YYYY-MM-DD'. */
export const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** 'YYYY-MM-DD' as a local Date at midnight. */
export const parseDay = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** That many calendar days on - by the calendar, so a DST night is still one day. */
export const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Whole days from one 'YYYY-MM-DD' to another (negative when it is earlier). */
export const daysBetween = (from, to) => Math.round((parseDay(to) - parseDay(from)) / 86400000);

const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * How a due date reads on a chip, Todoist-style: Today, Tomorrow, Yesterday,
 * a weekday for the coming six days, otherwise "25 Oct" - with the year only
 * when it is not this one. A time, when there is one, follows: "Today 15:00".
 * English on purpose: the screen around it is English, and a chip that says
 * "jeudi" beside "Due" reads as a bug.
 */
export function dueLabel(date, time, now) {
  if (!date) return time || '';
  const today = isoDay(now);
  const n = daysBetween(today, date);
  const d = parseDay(date);
  let label;
  if (n === 0) label = 'Today';
  else if (n === 1) label = 'Tomorrow';
  else if (n === -1) label = 'Yesterday';
  else if (n > 1 && n < 7) label = WD_SHORT[d.getDay()];
  else {
    label = `${d.getDate()} ${MON_SHORT[d.getMonth()]}`;
    if (d.getFullYear() !== now.getFullYear()) label += ` ${d.getFullYear()}`;
  }
  return time ? `${label} ${time}` : label;
}

/** "Thu 22 Oct": a day heading in Upcoming. */
export const longDay = (date) => {
  const d = parseDay(date);
  return `${WD_SHORT[d.getDay()]} ${d.getDate()} ${MON_SHORT[d.getMonth()]}`;
};

/**
 * "Due 15:00" - how a reminder card and the bell say when: the time alone
 * when it is today, otherwise the day as a chip says it ("Due Mon 15:00").
 */
export function dueLine(date, time, now) {
  const d = date ? String(date).slice(0, 10) : null;
  const t = time ? String(time).slice(0, 5) : null;
  if (!d) return t ? `Due ${t}` : 'Due now';
  if (d === isoDay(now)) return t ? `Due ${t}` : 'Due today';
  return `Due ${dueLabel(d, t, now)}`;
}

/** 'HH:MM' from a Date. */
export const clock = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/**
 * The day a time with no day means: today if that time is still ahead,
 * tomorrow if it has gone. "Meeting at 3pm" typed at nine is this afternoon;
 * typed at four, it can only be tomorrow's.
 */
export const dateForTime = (time, now) => (time > clock(now) ? isoDay(now) : isoDay(addDays(now, 1)));

/* ------------------------------------------------------------ the words */

const WEEKDAYS = [
  ['sunday'], ['monday', 'mon'], ['tuesday', 'tues', 'tue'], ['wednesday', 'weds', 'wed'],
  ['thursday', 'thurs', 'thur', 'thu'], ['friday', 'fri'], ['saturday']
];
const MONTHS = [
  ['january', 'jan'], ['february', 'feb'], ['march', 'mar'], ['april', 'apr'], ['may'],
  ['june', 'jun'], ['july', 'jul'], ['august', 'aug'], ['september', 'sept', 'sep'],
  ['october', 'oct'], ['november', 'nov'], ['december', 'dec']
];
const WEEKDAY_RE = WEEKDAYS.flat().join('|');
const MONTH_RE = MONTHS.flat().join('|');
const monthIndex = (w) => MONTHS.findIndex((names) => names.includes(w.toLowerCase()));
const weekdayIndex = (w) => WEEKDAYS.findIndex((names) => names.includes(w.toLowerCase()));
const COUNT_WORDS = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7 };

// A phrase starts at a word boundary and ends at one. Lookbehind rather than
// \b so "#" and "!" can open a phrase and "-" cannot sit just before one.
const START = '(?<![\\w#!/-])';
const END = '(?![\\w/:-])';
const ON = '(?:(?:on|by|due)\\s+)?';

/** A real calendar day, or null: 31/02 is not a date, and neither is 2026-13-01. */
const realDay = (y, m, d) => {
  const t = new Date(y, m, d);
  return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d ? isoDay(t) : null;
};

/** A day with no year given: this year's, unless it has passed - then next year's. */
const nextOccurrence = (m, d, now) => {
  const y = now.getFullYear();
  const here = realDay(y, m, d);
  if (here && here >= isoDay(now)) return here;
  return realDay(y + 1, m, d);
};

const fullYear = (y) => (y.length === 2 ? 2000 + Number(y) : Number(y));

/*
 * Each matcher: a pattern and what the match means. Tried in this order and
 * the first that matches wins its kind, so "next week" is read before
 * "week" could be anything else and an explicit date before a weekday.
 */
const DATE_MATCHERS = [
  { re: `${ON}(\\d{4})-(\\d{2})-(\\d{2})`,
    read: (m) => realDay(Number(m[1]), Number(m[2]) - 1, Number(m[3])) },
  // 25/10, 25/10/26, 25/10/2026 - DAY FIRST, as everywhere this app is used.
  { re: `${ON}(\\d{1,2})/(\\d{1,2})(?:/(\\d{4}|\\d{2}))?`,
    read: (m, now) => (m[3]
      ? realDay(fullYear(m[3]), Number(m[2]) - 1, Number(m[1]))
      : nextOccurrence(Number(m[2]) - 1, Number(m[1]), now)) },
  { re: `${ON}(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${MONTH_RE})(?:\\s+(\\d{4}))?`,
    read: (m, now) => (m[3]
      ? realDay(Number(m[3]), monthIndex(m[2]), Number(m[1]))
      : nextOccurrence(monthIndex(m[2]), Number(m[1]), now)) },
  { re: `${ON}(${MONTH_RE})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?`,
    read: (m, now) => (m[3]
      ? realDay(Number(m[3]), monthIndex(m[1]), Number(m[2]))
      : nextOccurrence(monthIndex(m[1]), Number(m[2]), now)) },
  { re: '(?:(?:by|due)\\s+)?(today)', read: (m, now) => isoDay(now) },
  { re: '(tonight)', read: (m, now) => isoDay(now), tonight: true },
  { re: `${ON}(tomorrow|tmrw|tmr)`, read: (m, now) => isoDay(addDays(now, 1)) },
  // Next week is next Monday, as Todoist has it: the start of it.
  { re: '(next\\s+week)', read: (m, now) => isoDay(addDays(now, ((8 - now.getDay()) % 7) || 7)) },
  { re: 'in\\s+(\\d{1,3}|a|an|one|two|three|four|five|six|seven)\\s+(days?|weeks?)',
    read: (m, now) => {
      const n = COUNT_WORDS[m[1].toLowerCase()] ?? Number(m[1]);
      return isoDay(addDays(now, /^w/i.test(m[2]) ? n * 7 : n));
    } },
  // A weekday is the NEXT one: "monday" said on a Monday is a week away,
  // because today already has a word of its own.
  { re: `(?:(?:on|by|due|this|next)\\s+)?(${WEEKDAY_RE})`,
    read: (m, now) => {
      const want = weekdayIndex(m[1]);
      return isoDay(addDays(now, ((want - now.getDay() + 7) % 7) || 7));
    } }
];

const TIME_MATCHERS = [
  // 3pm, 3 pm, 3:30pm, 9.30am, at 11am
  { re: '(?:(?:at|@)\\s*)?(\\d{1,2})(?:[:.](\\d{2}))?\\s*(am|pm)',
    read: (m) => {
      let h = Number(m[1]);
      const min = Number(m[2] || 0);
      if (h < 1 || h > 12 || min > 59) return null;
      if (m[3].toLowerCase() === 'pm' && h !== 12) h += 12;
      if (m[3].toLowerCase() === 'am' && h === 12) h = 0;
      return { time: `${pad(h)}:${pad(min)}`, meridiem: true };
    } },
  // 15:00, at 9:05 - the 24-hour clock, colon only.
  { re: '(?:(?:at|@)\\s*)?([01]?\\d|2[0-3]):([0-5]\\d)',
    read: (m) => ({ time: `${pad(Number(m[1]))}:${m[2]}`, meridiem: Number(m[1]) >= 13 }) },
  { re: '(?:at\\s+)?(noon|midday)', read: () => ({ time: '12:00', meridiem: true }) },
  // "at 3": nobody books a job for three in the morning. 1-7 are the
  // afternoon, 8-12 the morning-to-midday, 13-23 already say which.
  { re: 'at\\s+(\\d{1,2})',
    read: (m) => {
      const h = Number(m[1]);
      if (h > 23) return null;
      return { time: `${pad(h >= 1 && h <= 7 ? h + 12 : h)}:00`, meridiem: h >= 13 || (h >= 1 && h <= 7) };
    } }
];

const PRIORITY_RE = new RegExp(`${START}(?:p([1-4])|!([1-4]))${END}`, 'i');

/* ------------------------------------------- deadlines and durations */

/**
 * How long, as a chip and a row say it: "30 min", "2 h", "1 h 30". Whole
 * minutes in; '' for nothing.
 */
export function durationLabel(minutes) {
  const n = Math.round(Number(minutes) || 0);
  if (n <= 0) return '';
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h} h ${pad(m)}` : `${h} h`;
}

const HOURS = 'h(?:rs?|ours?)?';
const MINS = 'm(?:ins?|inutes?)?';
// Longest first: "for 1h30m" before "for 1h".
const DURATION_MATCHERS = [
  { re: `for\\s+(\\d{1,2})\\s*${HOURS}\\s*(?:and\\s+)?(\\d{1,2})\\s*${MINS}`, read: (m) => Number(m[1]) * 60 + Number(m[2]) },
  { re: `for\\s+(\\d{1,2})\\s*h(\\d{2})`, read: (m) => Number(m[1]) * 60 + Number(m[2]) },
  { re: `for\\s+(\\d{1,2}(?:[.,]\\d{1,2})?)\\s*${HOURS}`, read: (m) => Math.round(Number(m[1].replace(',', '.')) * 60) },
  { re: `for\\s+(\\d{1,4})\\s*${MINS}`, read: (m) => Number(m[1]) },
  { re: 'for\\s+(?:an?|one)\\s+hour\\s+and\\s+a\\s+half', read: () => 90 },
  { re: 'for\\s+(?:an?|one)\\s+hour', read: () => 60 },
  { re: 'for\\s+half\\s+an\\s+hour', read: () => 30 }
];
const MIN_DURATION = 5;
const MAX_DURATION = 24 * 60;

/** "{...}" read as a day by the same matchers as a due date, whole, or null. */
const deadlineDay = (inner, now) => {
  const s = String(inner || '').trim();
  for (const dm of DATE_MATCHERS) {
    const m = new RegExp(`^${dm.re}$`, 'i').exec(s);
    if (!m) continue;
    const day = dm.read(m, now);
    if (day) return day;
  }
  return null;
};

/* ------------------------------------------------------------ repeats */

export const WD_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// After "every", "sat" and "sun" are days: nobody repeats a sat nav. Longest first.
const RDAY = '(?:sunday|monday|tuesday|wednesday|thursday|friday|saturday'
  + '|tues|thurs|thur|weds|sun|mon|tue|wed|thu|fri|sat)s?';
const RDAYS = `${RDAY}(?:(?:\\s*,\\s*|\\s*&\\s*|\\s*\\/\\s*|\\s+and\\s+|\\s+)${RDAY})*`;
const RCOUNT = '(\\d{1,3}|other|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)';
const RCOUNT_WORDS = { ...COUNT_WORDS, other: 2, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
const UNIT_FREQ = { day: 'daily', week: 'weekly', month: 'monthly', year: 'yearly' };
const EVERY = 'every(!)?\\s+';

const countOf = (w) => (w ? (RCOUNT_WORDS[w.toLowerCase()] ?? Number(w)) : 1);
const repeatDay = (w) => {
  const k = w.toLowerCase();
  const i = weekdayIndex(k);
  if (i >= 0) return i;
  if (k === 'sun' || k === 'suns') return 0;
  if (k === 'sat' || k === 'sats') return 6;
  return k.endsWith('s') ? repeatDay(k.slice(0, -1)) : -1;
};
const dayList = (s) => [...new Set(String(s).toLowerCase().split(/[^a-z]+|\band\b/)
  .filter((w) => w && w !== 'and').map(repeatDay).filter((i) => i >= 0))].sort((a, b) => a - b);
const lastDayOf = (y, m) => new Date(y, m + 1, 0).getDate();

/*
 * Each: a pattern, and the repeat it says. `m[1]` is always the "!" of
 * "every!" (from completion). Specific before general, as with the dates:
 * "every 25 dec" before "every 25th", "every weekday" before "every week".
 */
const REPEAT_MATCHERS = [
  { re: `${EVERY}(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${MONTH_RE})`,
    read: (m, now) => {
      const day = nextOccurrence(monthIndex(m[3]), Number(m[2]), now);
      return day && { repeat: { freq: 'yearly', interval: 1 }, date: day };
    } },
  { re: `${EVERY}(${MONTH_RE})\\s+(\\d{1,2})(?:st|nd|rd|th)?`,
    read: (m, now) => {
      const day = nextOccurrence(monthIndex(m[2]), Number(m[3]), now);
      return day && { repeat: { freq: 'yearly', interval: 1 }, date: day };
    } },
  { re: `${EVERY}(weekday|workday|weekend)s?`,
    read: (m) => ({ repeat: { freq: 'weekly', interval: 1,
                              weekdays: /end/i.test(m[2]) ? [0, 6] : [1, 2, 3, 4, 5] } }) },
  { re: `${EVERY}(?:${RCOUNT}\\s+)?weeks?\\s+on\\s+(${RDAYS})`,
    read: (m) => ({ repeat: { freq: 'weekly', interval: countOf(m[2]), weekdays: dayList(m[3]) } }) },
  { re: `${EVERY}(?:${RCOUNT}\\s+)?months?\\s+on\\s+(?:the\\s+)?(?:(\\d{1,2})(?:st|nd|rd|th)?|(last)\\s+day)`,
    read: (m) => {
      const day = m[4] ? -1 : Number(m[3]);
      return day === -1 || (day >= 1 && day <= 31)
        ? { repeat: { freq: 'monthly', interval: countOf(m[2]), monthday: day } } : null;
    } },
  { re: `${EVERY}(?:the\\s+)?last\\s+day(?:\\s+of\\s+(?:the|every)\\s+month)?`,
    read: () => ({ repeat: { freq: 'monthly', interval: 1, monthday: -1 } }) },
  { re: `${EVERY}(?:the\\s+)?(\\d{1,2})(?:st|nd|rd|th)(?:\\s+of\\s+(?:the|every)\\s+month)?`,
    read: (m) => {
      const day = Number(m[2]);
      return day >= 1 && day <= 31 ? { repeat: { freq: 'monthly', interval: 1, monthday: day } } : null;
    } },
  { re: `${EVERY}(?:${RCOUNT}\\s+)?(day|week|month|year|fortnight)s?`,
    read: (m) => {
      const n = countOf(m[2]);
      if (!(n >= 1 && n <= 999)) return null;
      if (/^fortnight/i.test(m[3])) return { repeat: { freq: 'weekly', interval: 2 * n } };
      return { repeat: { freq: UNIT_FREQ[m[3].toLowerCase()], interval: n } };
    } },
  { re: `${EVERY}(${RDAYS})`,
    read: (m) => {
      const days = dayList(m[2]);
      return days.length ? { repeat: { freq: 'weekly', interval: 1, weekdays: days } } : null;
    } },
  { re: '()(daily)', read: () => ({ repeat: { freq: 'daily', interval: 1 } }) },
  // "after 3 days": the next one is three days after this one is done.
  { re: `()after\\s+(\\d{1,3}|a|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\\s+(day|week|month|year)s?`,
    read: (m) => {
      const n = countOf(m[2]);
      return n >= 1 && n <= 999
        ? { repeat: { freq: UNIT_FREQ[m[3].toLowerCase()], interval: n }, fromDone: true } : null;
    } }
];

// "until 31 dec": the explicit dates only - "until monday" is a deadline, not an end.
const UNTIL_MATCHERS = DATE_MATCHERS.slice(0, 4).map((dm) => ({
  re: `until\\s+${dm.re.replace(ON, '')}`, read: dm.read
}));

/**
 * The first day a repeat falls on, on or after today - or tomorrow, when the
 * time it is set for has already gone today. A month without a 31st takes
 * its last day, as Todoist does.
 */
export function firstOccurrence(repeat, now, time = null) {
  if (!repeat) return null;
  const from = time && time <= clock(now) ? addDays(now, 1)
    : new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (repeat.freq === 'weekly' && Array.isArray(repeat.weekdays) && repeat.weekdays.length) {
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(from, i);
      if (repeat.weekdays.includes(d.getDay())) return isoDay(d);
    }
  }
  if (repeat.freq === 'monthly' && repeat.monthday) {
    for (let k = 0; k < 13; k += 1) {
      const y = from.getFullYear() + Math.floor((from.getMonth() + k) / 12);
      const mo = (from.getMonth() + k) % 12;
      const last = lastDayOf(y, mo);
      const d = new Date(y, mo, repeat.monthday === -1 ? last : Math.min(repeat.monthday, last));
      if (d >= from) return isoDay(d);
    }
  }
  return isoDay(from);
}

export const ordinal = (n) => {
  if (n === -1) return 'last day';
  const t = n % 100;
  const s = t >= 11 && t <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  return `${n}${s}`;
};
const joinAnd = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const shortDate = (date, now) => {
  const d = parseDay(date);
  const y = now && d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`;
  return `${d.getDate()} ${MON_SHORT[d.getMonth()]}${y}`;
};

/**
 * A repeat in words, for the chip and the row's tooltip: "Every Monday",
 * "Every Mon, Wed and Fri", "Every 2 weeks on Friday", "Every month on the
 * 15th", "Every 3 days after done", "... until 31 Dec". `dueDate` names the
 * day a yearly one falls on. English, like the due labels.
 */
export function repeatText(repeat, { now = null, dueDate = null } = {}) {
  const unit = repeat && { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[repeat.freq];
  if (!unit) return '';
  const n = Math.max(1, Number(repeat.interval) || 1);
  const every = n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
  const days = Array.isArray(repeat.weekdays) ? [...repeat.weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)) : [];
  let text = every;
  if (repeat.freq === 'weekly' && days.length) {
    const key = days.join();
    if (n === 1 && key === '1,2,3,4,5') text = 'Every weekday';
    else if (n === 1 && key === '6,0') text = 'Every weekend';
    else {
      const names = days.length === 1 ? [WD_LONG[days[0]]] : days.map((d) => WD_SHORT[d]);
      text = n === 1 ? `Every ${joinAnd(names)}` : `${every} on ${joinAnd(names)}`;
    }
  } else if (repeat.freq === 'monthly' && repeat.monthday) {
    text = `${every} on the ${ordinal(Number(repeat.monthday))}`;
  } else if (repeat.freq === 'yearly' && dueDate) {
    const d = parseDay(dueDate);
    text = `${every} on ${d.getDate()} ${MON_SHORT[d.getMonth()]}`;
  }
  if (repeat.from === 'done') text += ' after done';
  if (repeat.until) text += ` until ${shortDate(repeat.until, now)}`;
  return text;
}

/** The first match of `re` in `text` that does not overlap a span already taken. */
function firstFree(re, text, taken) {
  const g = new RegExp(`${START}${re}${END}`, 'gi');
  let m;
  while ((m = g.exec(text)) !== null) {
    const s = m.index;
    const e = s + m[0].length;
    if (!taken.some(([a, b]) => s < b && e > a)) return { m, s, e };
    g.lastIndex = s + 1;
  }
  return null;
}

/** Which of the contractor's tickets a typed number means, if exactly one. */
function findTicket(text, tickets, taken) {
  if (!tickets || !tickets.length) return null;
  const full = new RegExp(`${START}([a-z]{2,6}-\\d{2,6}(?:-\\d{1,6})?)${END}`, 'gi');
  let m;
  while ((m = full.exec(text)) !== null) {
    const want = m[1].toLowerCase();
    const t = tickets.find((x) => String(x.ticket_number || '').toLowerCase() === want);
    const s = m.index;
    const e = s + m[0].length;
    if (t && !taken.some(([a, b]) => s < b && e > a)) return { ticket: t, s, e };
  }
  // "#42" or "#0042": the ticket whose number ENDS in that number.
  const short = /(?<![\w#])#(\d{1,6})(?![\w])/g;
  while ((m = short.exec(text)) !== null) {
    const n = Number(m[1]);
    const hits = tickets.filter((x) => {
      const tail = /(\d+)$/.exec(String(x.ticket_number || ''));
      return tail && Number(tail[1]) === n;
    });
    if (hits.length === 1) return { ticket: hits[0], s: m.index, e: m.index + m[0].length };
  }
  return null;
}

/** A name as it is compared: lower case, letters and digits only. */
export const nameKey = (s) => String(s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

const overlaps = (taken, s, e) => taken.some(([a, b]) => s < b && e > a);

/**
 * The first `mark`-word in `text` (mark is '#', '/' or '@') that names one of
 * `things`. A whole name wins first, longest first, so "#Site visits" is the
 * list "Site visits" and not "Site"; then, if `loose`, the one word after the
 * mark is matched against the start of a name, then anywhere in one, the
 * shortest such name winning ("#site" is "Site", not "Site visits"). Returns { thing, s, e } or, when
 * `keepWord` and nothing matched, { word, s, e } - which is how a new label
 * is found.
 */
function findNamed(text, mark, things, taken, { loose = false, keepWord = false } = {}) {
  const esc = mark === '/' ? '\\/' : mark === '+' ? '\\+' : mark;
  const before = mark === '@' ? '[\\w@.]' : mark === '/' ? '[\\w/:.-]' : mark === '+' ? '[\\w+]' : '[\\w#&]';
  const re = new RegExp(`(?<!${before})${esc}(\\p{L}[\\p{L}\\p{N}_-]*)`, 'gu');
  const named = (things || []).filter((t) => t && t.name)
    .slice().sort((a, b) => String(b.name).length - String(a.name).length);
  let m;
  while ((m = re.exec(text)) !== null) {
    const s = m.index;
    const after = text.slice(s + 1);
    // A whole name, spaces and all, ending at a word boundary.
    const whole = named.find((t) => {
      const n = String(t.name);
      return after.slice(0, n.length).toLowerCase() === n.toLowerCase()
        && !/[\p{L}\p{N}_]/u.test(after.charAt(n.length));
    });
    if (whole) {
      const e = s + 1 + String(whole.name).length;
      if (!overlaps(taken, s, e)) return { thing: whole, s, e };
      continue;
    }
    const e = s + m[0].length;
    if (overlaps(taken, s, e)) continue;
    const word = nameKey(m[1]);
    const exact = named.filter((t) => nameKey(t.name) === word);
    if (exact.length) return { thing: exact[0], s, e };
    if (loose && word) {
      const byLength = (a, b) => String(a.name).length - String(b.name).length;
      const starts = named.filter((t) => nameKey(t.name).startsWith(word)).sort(byLength);
      const inside = named.filter((t) => nameKey(t.name).includes(word)).sort(byLength);
      const pick = starts[0] || inside[0];
      if (pick) return { thing: pick, s, e };
    }
    if (keepWord) return { word: m[1], s, e };
  }
  return null;
}

/**
 * Read a line typed into the quick-add box.
 *
 *   parseQuickAdd('Order cable tomorrow 3pm p1', { now, tickets })
 *   -> { title: 'Order cable', due_date: '2026-10-21', due_time: '15:00',
 *        priority: 1, ticket: null, chips: [...] }
 *
 * `ignore` is a set of kinds ('date', 'time', 'priority', 'ticket', 'list',
 * 'section') the person has taken back by removing the chip: those words
 * stay in the title. A label is taken back one at a time, as 'label:' and
 * its nameKey, because one line can carry several.
 * Every field is null when nothing said it; priority is null rather than 4 so
 * the caller can tell "said nothing" from "said p4".
 *
 * `lists` are [{ id, name, sections: [{ id, name }] }], `labels` [{ id, name }]
 * and `defaultListId` the list a "/Section" with no "#List" belongs to. Out
 * come `list` and `section` (the objects passed in, or null) and `labels`:
 * [{ id, name, isNew }] - `id` null and `isNew` true for one nobody has made.
 *
 * `repeat` is the contract's shape - { freq, interval, weekdays?, monthday?,
 * from, until? } - or null, `repeat_text` the same in words, and 'repeat'
 * is one more kind that `ignore` can take back. A repeat always comes with
 * a due_date: the server refuses one without.
 *
 * `people` is the directory, [{ key, name }], for "+Name"; the list's own
 * `members` are read off the list. Out comes `assignee`: { key, name, member }
 * or null, and 'assignee' is one more kind `ignore` can take back.
 */
export function parseQuickAdd(text, {
  now, tickets = [], ignore = [], lists = [], labels = [], defaultListId = null, people = []
} = {}) {
  const src = String(text || '');
  const skip = new Set(ignore);
  const taken = [];
  const chips = [];
  const out = { title: '', due_date: null, due_time: null, priority: null, ticket: null,
                list: null, section: null, labels: [], repeat: null, repeat_text: null,
                assignee: null, deadline: null, duration_minutes: null, chips };
  if (!now) now = new Date();

  if (!skip.has('ticket')) {
    const hit = findTicket(src, tickets, taken);
    if (hit) {
      taken.push([hit.s, hit.e]);
      out.ticket = hit.ticket;
      chips.push({ kind: 'ticket', label: hit.ticket.ticket_number, text: src.slice(hit.s, hit.e) });
    }
  }

  // Lists, sections and labels before the dates and times, so "#Tomorrow" is
  // a list called Tomorrow and "@today" a label rather than a due date.
  if (!skip.has('list') && lists.length) {
    const hit = findNamed(src, '#', lists, taken, { loose: true });
    if (hit) {
      taken.push([hit.s, hit.e]);
      out.list = hit.thing;
      chips.push({ kind: 'list', label: hit.thing.name, text: src.slice(hit.s, hit.e), id: hit.thing.id,
                   color: hit.thing.color || null });
    }
  }
  if (!skip.has('section')) {
    const home = out.list || lists.find((l) => l.id === defaultListId) || null;
    const sections = home && Array.isArray(home.sections) ? home.sections : [];
    const hit = sections.length ? findNamed(src, '/', sections, taken, { loose: true }) : null;
    if (hit) {
      taken.push([hit.s, hit.e]);
      out.section = hit.thing;
      chips.push({ kind: 'section', label: hit.thing.name, text: src.slice(hit.s, hit.e), id: hit.thing.id });
    }
  }
  // "+Marco": a member of the list it goes into first, then anybody in the
  // directory - for whom it is a send rather than an assignment.
  if (!skip.has('assignee')) {
    const home = out.list || lists.find((l) => l.id === defaultListId) || null;
    const members = home && Array.isArray(home.members) ? home.members.filter((m) => m && m.key) : [];
    const keys = new Set(members.map((m) => m.key));
    const others = (people || []).filter((p) => p && p.key && !keys.has(p.key));
    let hit = members.length ? findNamed(src, '+', members, taken, { loose: true }) : null;
    let member = Boolean(hit);
    if (!hit && others.length) { hit = findNamed(src, '+', others, taken, { loose: true }); member = false; }
    if (hit) {
      taken.push([hit.s, hit.e]);
      out.assignee = { key: hit.thing.key, name: hit.thing.name, member };
      chips.push({ kind: 'assignee', label: hit.thing.name, text: src.slice(hit.s, hit.e),
                   who: hit.thing.key, member });
    }
  }
  // A label taken back is passed over - left in the title - but must not be
  // found again on the next time round, hence a second list of spans.
  const passed = [];
  for (let guard = 0; guard < 20; guard += 1) {
    const hit = findNamed(src, '@', labels, taken.concat(passed), { keepWord: true });
    if (!hit) break;
    const name = hit.thing ? hit.thing.name : hit.word;
    const key = `label:${nameKey(name)}`;
    if (skip.has(key)) { passed.push([hit.s, hit.e]); continue; }
    taken.push([hit.s, hit.e]);
    if (out.labels.some((l) => nameKey(l.name) === nameKey(name))) continue;
    const label = { id: hit.thing ? hit.thing.id : null, name, isNew: !hit.thing };
    out.labels.push(label);
    chips.push({ kind: 'label', key, label: name, text: src.slice(hit.s, hit.e), id: label.id,
                 isNew: label.isNew, color: hit.thing ? hit.thing.color || null : null });
  }

  // "{fri}": the deadline, before any date is read, so the day inside the
  // braces is not also taken for the due date. Whatever else is in braces -
  // "{maybe}", a deadline whose chip was taken back - is QUIET: left in the
  // title and not read for a date, a time or a duration either.
  const quiet = [];
  {
    const re = /\{([^{}\n]{1,40})\}/g;
    let m;
    while ((m = re.exec(src)) !== null) {
      const s = m.index;
      const e = s + m[0].length;
      if (overlaps(taken, s, e)) continue;
      const day = !skip.has('deadline') && !out.deadline ? deadlineDay(m[1], now) : null;
      if (!day) { quiet.push([s, e]); continue; }
      taken.push([s, e]);
      out.deadline = day;
      chips.push({ kind: 'deadline', label: dueLabel(day, null, now), text: m[0] });
    }
  }
  // "for 2h": how long it takes.
  if (!skip.has('duration')) {
    for (const dm of DURATION_MATCHERS) {
      const hit = firstFree(dm.re, src, taken.concat(quiet));
      if (!hit) continue;
      const n = dm.read(hit.m);
      if (!(Number.isInteger(n) && n >= MIN_DURATION && n <= MAX_DURATION)) continue;
      taken.push([hit.s, hit.e]);
      out.duration_minutes = n;
      chips.push({ kind: 'duration', label: durationLabel(n), text: src.slice(hit.s, hit.e) });
      break;
    }
  }

  if (!skip.has('priority')) {
    const m = PRIORITY_RE.exec(src);
    if (m && !taken.some(([a, b]) => m.index < b && m.index + m[0].length > a)) {
      taken.push([m.index, m.index + m[0].length]);
      out.priority = Number(m[1] || m[2]);
      chips.push({ kind: 'priority', label: `P${out.priority}`, text: m[0] });
    }
  }

  // A repeat before the dates, so "every monday" is a repeat and not a
  // Monday. Taken back, its words are HELD: left in the title, and not read
  // again as a day either - "bins every monday" with the chip removed is
  // just what was typed, not a to-do for next Monday.
  const held = [];
  let rep = null;
  for (const rm of REPEAT_MATCHERS) {
    const hit = firstFree(rm.re, src, taken);
    if (!hit) continue;
    const r = rm.read(hit.m, now);
    if (!r) continue;
    rep = { ...r, s: hit.s, e: hit.e, fromDone: Boolean(r.fromDone || hit.m[1]) };
    break;
  }
  if (rep) {
    const spans = [[rep.s, rep.e]];
    let until = null;
    for (const um of UNTIL_MATCHERS) {
      const hit = firstFree(um.re, src, taken.concat(spans));
      if (!hit) continue;
      const day = um.read(hit.m, now);
      if (!day) continue;
      spans.push([hit.s, hit.e]);
      until = day;
      break;
    }
    if (skip.has('repeat')) {
      held.push(...spans);
    } else {
      taken.push(...spans);
      out.repeat = { ...rep.repeat, from: rep.fromDone ? 'done' : 'due' };
      if (until) out.repeat.until = until;
      chips.push({ kind: 'repeat', label: '', text: spans.map(([s, e]) => src.slice(s, e)).join(' ') });
    }
  }
  const blocked = () => taken.concat(held, quiet);

  let tonight = false;
  let dateText = null;
  if (!skip.has('date')) {
    for (const dm of DATE_MATCHERS) {
      const hit = firstFree(dm.re, src, blocked());
      if (!hit) continue;
      const day = dm.read(hit.m, now);
      if (!day) continue;
      taken.push([hit.s, hit.e]);
      out.due_date = day;
      tonight = Boolean(dm.tonight);
      dateText = src.slice(hit.s, hit.e);
      break;
    }
  }

  let meridiem = false;
  if (!skip.has('time')) {
    for (const tm of TIME_MATCHERS) {
      const hit = firstFree(tm.re, src, blocked());
      if (!hit) continue;
      const t = tm.read(hit.m);
      if (!t) continue;
      taken.push([hit.s, hit.e]);
      out.due_time = t.time;
      meridiem = t.meridiem;
      chips.push({ kind: 'time', label: '', text: src.slice(hit.s, hit.e) });
      break;
    }
  }

  // "Tonight" is the evening: 8 o'clock unless a time was given, and "at 9"
  // tonight is nine at night, not in the morning.
  if (tonight) {
    if (!out.due_time) out.due_time = '20:00';
    else if (!meridiem && Number(out.due_time.slice(0, 2)) < 12) {
      out.due_time = `${pad(Number(out.due_time.slice(0, 2)) + 12)}${out.due_time.slice(2)}`;
    }
  }

  // A repeat with no day said starts on its first day ("every 25 dec" said
  // its own); a time with no day means the next time that time comes round.
  const dateSaid = Boolean(out.due_date);
  if (out.repeat && !out.due_date) out.due_date = rep.date || firstOccurrence(out.repeat, now, out.due_time);
  if (out.due_time && !out.due_date) out.due_date = dateForTime(out.due_time, now);
  if (out.repeat) {
    out.repeat_text = repeatText(out.repeat, { now, dueDate: out.due_date });
    const chip = chips.find((c) => c.kind === 'repeat');
    chip.label = `↻ ${out.repeat_text}`;
    chip.first = out.due_date;
  }

  if (dateSaid) {
    chips.unshift({ kind: 'date', label: dueLabel(out.due_date, tonight ? out.due_time : null, now),
                    text: dateText });
  }
  const timeChip = chips.find((c) => c.kind === 'time');
  if (timeChip) timeChip.label = dateSaid ? out.due_time : dueLabel(out.due_date, out.due_time, now);
  if (tonight && timeChip) {
    // One chip says "Today 20:00" already; the time chip says only the time.
    chips[0].label = dueLabel(out.due_date, null, now);
  }

  // Chips in the order Todoist shows them: when, then how urgent, then what,
  // then where it goes and how it is tagged.
  const rank = { date: 0, time: 1, duration: 1.2, deadline: 1.4, repeat: 1.5, priority: 2, ticket: 3, list: 4,
                 section: 5, label: 6, assignee: 7 };
  chips.sort((a, b) => rank[a.kind] - rank[b.kind]);

  // The title is what is left, with the words that were read taken out.
  let title = '';
  let at = 0;
  for (const [s, e] of taken.slice().sort((a, b) => a[0] - b[0])) {
    title += `${src.slice(at, s)} `;
    at = e;
  }
  title += src.slice(at);
  out.title = title.replace(/\s+/g, ' ').trim();
  return out;
}
